import React, { Component } from "react";
import {
  closeChat,
  sendMessageAdmin,
  getChatListAdmin,
  getChatListAdminById,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C,
  Avatar,
  Btn,
  Chip,
  ConfirmDialog, Pager} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Guest chat (F1 · F2 of the handoff).
 *
 * Three columns inside one panel: the conversation list, the thread, and a
 * context rail. The design's rail carries a full guest profile — room number,
 * loyalty tier, folio, stay dates, notes. None of that is on the chat payload
 * and there is no endpoint here that returns it, so the rail shows what this
 * conversation actually knows: who it is with, whether it is open, when it
 * started, and how much has been said.
 */

// Scoped styles. Hover and scrollbars can't be expressed inline, and the
// thread column has to scroll independently of the page.
const CSS = `
.chat-wrap{display:grid;grid-template-columns:320px 1fr 300px;
  height:calc(100vh - 208px);min-height:520px;background:#fff;
  border:1px solid ${C.tint};border-radius:18px;overflow:hidden}
.chat-col{display:flex;flex-direction:column;overflow:hidden;min-width:0}
.chat-scroll{flex:1;overflow-y:auto;overflow-x:hidden}
.chat-scroll::-webkit-scrollbar{width:8px}
.chat-scroll::-webkit-scrollbar-thumb{background:#e6d8c4;border-radius:4px}
.chat-row{display:flex;gap:12px;padding:13px 14px;border-radius:14px;
  border:1px solid transparent;cursor:pointer}
.chat-row:hover{background:#fff;border-color:${C.tint}}
.chat-row.is-active{background:#fff;border-color:${C.tint}}
.chat-field{flex:1;border:0;outline:none;background:transparent;font-size:15px;
  color:${C.ink};font-family:inherit;min-width:0}
.chat-field::placeholder{color:${C.taupeLight}}
.chat-send{width:42px;height:42px;border:0;border-radius:21px;background:${C.brown};
  color:#fff;display:grid;place-items:center;cursor:pointer;flex:0 0 42px}
.chat-send:disabled{opacity:.45;cursor:default}
`;

class ChatList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      limit: 25,
      pagination: null,
      items: [],
      isLoaded: false,
      selectedChat: null,
      selectedChatHistory: [],
      chatMasterId: "",
      message: "",
      searchQuery: "",
      filter: "all",
      isSending: false,
      confirmClose: false,
    };
    this.messagesEndRef = React.createRef();
  }

  componentDidMount() {
    this.getAllChats();
    // Start auto-refresh polling every 15 seconds
    this.pollInterval = setInterval(() => {
      this.pollForUpdates();
    }, 15000);
  }

  componentWillUnmount() {
    // Clean up polling interval
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedChatHistory !== this.state.selectedChatHistory) {
      this.scrollToBottom();
    }
  }

  pollForUpdates = () => {
    // Skip polling if user is typing a message (to avoid disruption)
    if (this.state.message && this.state.message.trim().length > 0) {
      return;
    }

    // Refresh chat list silently (without loading state), keeping the page the
    // user is currently viewing.
    getChatListAdmin(this.state.page, this.state.limit).then((result) => {
      if (result && result.chatData) {
        this.setState({
          items: result.chatData,
          pagination: result.pagination || this.state.pagination,
        });
      }
    });

    // If a chat is selected, refresh its messages
    if (this.state.selectedChat) {
      getChatListAdminById(this.state.selectedChat.chatId.toString())
        .then((result) => {
          if (result && result.chatHistory) {
            // Only update if there are new messages
            if (result.chatHistory.length !== this.state.selectedChatHistory.length) {
              this.setState({
                selectedChatHistory: result.chatHistory,
                chatMasterId: result.data._id,
              });
            }
          }
        })
        .catch(() => {});
    }
  };

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  getAllChats = (page = this.state.page, limit = this.state.limit) => {
    getChatListAdmin(page, limit).then((result) => {
      this.setState({
        isLoaded: true,
        items: result.chatData || [],
        pagination: result.pagination || null,
        page,
        limit,
      }, () => {
        // After chats are loaded, check for deep link
        const urlParams = new URLSearchParams(window.location.search);
        const chatId = urlParams.get('chatId');
        if (chatId && !this.state.selectedChat) {
          // Convert both to string for comparison (handles ObjectId vs string mismatch)
          const targetChat = this.state.items.find(chat =>
            String(chat.chatId) === String(chatId)
          );
          if (targetChat) {
            this.selectChat(targetChat);
          } else {
            console.log('Chat not found. Available chats:', this.state.items.map(c => c.chatId));
            console.log('Looking for chatId:', chatId);
          }
        }
      });
    });
  };

  selectChat = (chat) => {
    this.setState({ selectedChat: chat });
    this.loadChatHistory(chat);
  };

  loadChatHistory = (chat) => {
    if (chat.chatId) {
      getChatListAdminById(chat.chatId.toString())
        .then((result) => {
          this.setState({
            selectedChatHistory: result.chatHistory || [],
            chatMasterId: result.data._id,
          });
        })
        .catch((error) => {
          toast.error("Failed to load chat history");
        });
    }
  };

  handleCloseChat = (chatId, status) => {
    if (status === "Close") {
      toast.info("Chat is already closed");
      return;
    }
    closeChat(chatId)
      .then((result) => {
        this.getAllChats();
        toast.success("Chat closed successfully");
        if (this.state.selectedChat?.chatId === chatId) {
          this.setState({
            selectedChat: { ...this.state.selectedChat, chatStatus: "Close" },
          });
        }
      })
      .catch((error) => {
        toast.error("Failed to close chat");
      });
  };

  confirmCloseThread = () => {
    const { selectedChat } = this.state;
    this.setState({ confirmClose: false });
    if (!selectedChat) return;
    this.handleCloseChat(selectedChat.chatId.toString(), selectedChat.chatStatus);
  };

  handleSendMessage = (e) => {
    e.preventDefault();
    if (!this.state.message.trim() || this.state.isSending) return;

    this.setState({ isSending: true });

    const userObject = {
      message: this.state.message,
      chatMasterId: this.state.chatMasterId,
    };

    sendMessageAdmin(userObject)
      .then((response) => {
        if (response.status === 200) {
          this.setState({ message: "" });
          this.loadChatHistory(this.state.selectedChat);
          this.getAllChats();
        } else {
          toast.error(response.message || "Failed to send message");
        }
      })
      .catch((error) => {
        toast.error("Failed to send message");
      })
      .finally(() => {
        this.setState({ isSending: false });
      });
  };

  formatTime = (date) => {
    if (!date) return "";
    return moment.utc(date).tz("Africa/Lubumbashi").format("hh:mm A");
  };

  formatDate = (date) => {
    if (!date) return "";
    const msgDate = moment.utc(date).tz("Africa/Lubumbashi");
    const today = moment().tz("Africa/Lubumbashi").startOf("day");
    const yesterday = moment().tz("Africa/Lubumbashi").subtract(1, "day").startOf("day");

    if (msgDate.isSame(today, "day")) return "Today";
    if (msgDate.isSame(yesterday, "day")) return "Yesterday";
    return msgDate.format("DD MMM YYYY");
  };

  formatListTime = (date) => {
    if (!date) return "";
    const msgDate = moment.utc(date).tz("Africa/Lubumbashi");
    // A conversation with no messages has no real timestamp — the API falls
    // back to the epoch, and "01/01/70" beside a guest's name is noise.
    if (msgDate.year() < 2000) return "";
    const today = moment().tz("Africa/Lubumbashi").startOf("day");

    if (msgDate.isSame(today, "day")) {
      return msgDate.format("hh:mm A");
    }
    return msgDate.format("DD/MM/YY");
  };

  getFilteredChats = () => {
    const { items, searchQuery, filter } = this.state;
    let list = items;

    if (filter === "open") list = list.filter((c) => c.chatStatus === "Open");
    if (filter === "closed") list = list.filter((c) => c.chatStatus !== "Open");

    if (!searchQuery.trim()) return list;
    return list.filter((chat) =>
      (chat.chatFromName || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // --- Column 1: conversations ---------------------------------------------

  renderList() {
    const { items, selectedChat, isLoaded, filter, searchQuery } = this.state;
    const rows = this.getFilteredChats();
    const openCount = items.filter((c) => c.chatStatus === "Open").length;

    // These filters run over the loaded page, so their counts are page counts.
    const tabs = [
      { value: "all", label: "All", count: items.length },
      { value: "open", label: "Open", count: openCount },
      { value: "closed", label: "Closed", count: items.length - openCount },
    ];

    return (
      <div className="chat-col" style={{ borderRight: `1px solid ${C.border}`, background: C.surfaceAlt }}>
        <div style={{ padding: 16, borderBottom: `1px solid ${C.border}` }}>
          <div
            style={{
              display: "flex", alignItems: "center", gap: 10, height: 40,
              padding: "0 14px", borderRadius: 20, background: "#fff",
              border: `1px solid ${C.border}`,
            }}
          >
            <span className="ms" style={{ fontSize: 18, color: C.taupeLight }}>search</span>
            <input
              className="chat-field"
              style={{ fontSize: 14 }}
              value={searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              placeholder="Search guests"
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, padding: "12px 16px", flexWrap: "wrap" }}>
          {tabs.map((t) => (
            <span
              key={t.value}
              onClick={() => this.setState({ filter: t.value })}
              style={{
                padding: "7px 14px", borderRadius: 16, fontSize: 13, cursor: "pointer",
                background: filter === t.value ? C.brown : "#fff",
                color: filter === t.value ? "#fff" : C.taupe,
                border: filter === t.value ? "1px solid transparent" : `1px solid ${C.border}`,
              }}
            >
              {t.label} · {t.count}
            </span>
          ))}
        </div>

        <div className="chat-scroll" style={{ padding: "0 10px 12px" }}>
          {!isLoaded ? (
            <div style={{ padding: "40px 14px", color: C.taupeLight, fontSize: 14 }}>
              Loading conversations…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: "40px 14px", color: C.taupeLight, fontSize: 14 }}>
              {items.length === 0
                ? "No guest has started a conversation yet."
                : "No conversation matches this filter."}
            </div>
          ) : (
            rows.map((chat, i) => {
              const active = selectedChat && selectedChat.chatId === chat.chatId;
              return (
                <div
                  key={chat.chatId || i}
                  className={`chat-row ${active ? "is-active" : ""}`}
                  onClick={() => this.selectChat(chat)}
                >
                  <Avatar name={chat.chatFromName} size={40} />
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span
                        style={{
                          fontSize: 15, fontWeight: 500, color: C.ink,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        {chat.chatFromName || "Unknown guest"}
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: 12, color: C.taupeLight, flex: "0 0 auto" }}>
                        {this.formatListTime(chat.chatLastMessageTime || chat.updatedAt)}
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: 3, fontSize: 13, color: C.taupe,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                    >
                      {chat.chatLastMessage || "No messages yet"}
                    </div>
                  </div>
                  {chat.chatUnreadCount > 0 ? (
                    <span
                      title={`${chat.chatUnreadCount} unread`}
                      style={{
                        width: 20, height: 20, borderRadius: 10, alignSelf: "center",
                        background: C.green, color: "#fff", fontSize: 11, fontWeight: 700,
                        display: "grid", placeItems: "center", flex: "0 0 20px",
                      }}
                    >
                      {chat.chatUnreadCount}
                    </span>
                  ) : (
                    chat.chatStatus === "Open" && (
                      <span
                        title="Open"
                        style={{
                          width: 8, height: 8, borderRadius: 4, alignSelf: "center",
                          background: C.green, flex: "0 0 8px",
                        }}
                      />
                    )
                  )}
                </div>
              );
            })
          )}

          {this.state.pagination && this.state.pagination.totalPages > 1 && (
            <div style={{ padding: "4px 12px 10px", borderTop: `1px solid ${C.tint}` }}>
              <Pager
                page={this.state.pagination.currentPage}
                totalPages={this.state.pagination.totalPages}
                total={this.state.pagination.totalCount}
                limit={this.state.pagination.limit}
                onPage={(n) => this.getAllChats(n, this.state.limit)}
                noun="chats"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Column 2: the thread -------------------------------------------------

  renderMessages() {
    const { selectedChatHistory } = this.state;
    let lastDate = null;

    if (selectedChatHistory.length === 0) {
      return (
        <div style={{ margin: "auto", color: C.taupeLight, fontSize: 14 }}>
          No messages in this conversation yet.
        </div>
      );
    }

    return selectedChatHistory.map((m, index) => {
      const messageDate = this.formatDate(m.createdDate);
      const showDivider = messageDate !== lastDate;
      lastDate = messageDate;
      const mine = m.fromUser?.role === 1;

      return (
        <React.Fragment key={index}>
          {showDivider && (
            <div
              style={{
                alignSelf: "center", padding: "5px 13px", borderRadius: 11,
                background: "#efe2ce", fontSize: 12, color: C.taupe,
              }}
            >
              {messageDate}
            </div>
          )}
          <div style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "62%" }}>
            <div
              style={{
                padding: "13px 17px",
                borderRadius: mine ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                background: mine ? C.brown : "#fff",
                border: mine ? "1px solid transparent" : `1px solid ${C.tint}`,
                color: mine ? "#fff" : C.ink,
                fontSize: 15, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}
            >
              {m.messageText}
            </div>
            <div
              style={{
                marginTop: 5, fontSize: 12, color: C.taupeLight,
                textAlign: mine ? "right" : "left",
              }}
            >
              {this.formatTime(m.createdDate)}
            </div>
          </div>
        </React.Fragment>
      );
    });
  }

  renderThread() {
    const { selectedChat, message, isSending } = this.state;
    const write = canWrite();

    if (!selectedChat) {
      return (
        <div
          className="chat-col"
          style={{
            background: C.canvas, alignItems: "center", justifyContent: "center",
            textAlign: "center", padding: 40,
          }}
        >
          <span className="ms" style={{ fontSize: 34, color: C.sand, background: "#fff", padding: 18, borderRadius: 22 }}>
            forum
          </span>
          <div style={{ marginTop: 18, fontSize: 18, fontWeight: 500, color: C.ink }}>
            Select a conversation
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: C.taupe, maxWidth: 320 }}>
            Pick a guest from the list to read the thread and reply.
          </div>
        </div>
      );
    }

    const open = selectedChat.chatStatus === "Open";

    return (
      <div className="chat-col">
        <div
          style={{
            display: "flex", alignItems: "center", gap: 12, padding: "14px 24px",
            borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt,
          }}
        >
          <Avatar name={selectedChat.chatFromName} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 500, color: C.ink }}>
              {selectedChat.chatFromName || "Unknown guest"}
            </div>
            <div
              style={{
                marginTop: 2, display: "flex", alignItems: "center", gap: 6,
                fontSize: 13, color: open ? C.green : C.taupeLight,
              }}
            >
              <span
                style={{
                  width: 7, height: 7, borderRadius: 4,
                  background: open ? C.green : C.sand,
                }}
              />
              {open ? "Open conversation" : "Closed conversation"}
            </div>
          </div>
        </div>

        <div
          className="chat-scroll"
          style={{
            padding: "22px 24px", display: "flex", flexDirection: "column",
            gap: 14, background: C.canvas,
          }}
        >
          {this.renderMessages()}
          <div ref={this.messagesEndRef} />
        </div>

        <div style={{ padding: "14px 24px 18px", borderTop: `1px solid ${C.border}`, background: C.surfaceAlt }}>
          {open && write ? (
            <form
              onSubmit={this.handleSendMessage}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "0 8px 0 18px", height: 56, background: "#fff",
                border: `1px solid ${C.border}`, borderRadius: 28,
              }}
            >
              <input
                className="chat-field"
                value={message}
                onChange={(e) => this.setState({ message: e.target.value })}
                placeholder="Type a reply…"
              />
              <button
                type="submit"
                className="chat-send"
                title="Send reply"
                aria-label="Send reply"
                disabled={!message.trim() || isSending}
              >
                <span className="ms" style={{ fontSize: 20 }}>send</span>
              </button>
            </form>
          ) : (
            <div
              style={{
                display: "flex", alignItems: "center", gap: 10, height: 56,
                padding: "0 18px", borderRadius: 28, background: "#fff",
                border: `1px dashed ${C.sand}`, color: C.taupeLight, fontSize: 14,
              }}
            >
              <span className="ms" style={{ fontSize: 19 }}>lock</span>
              {open
                ? "Your account is view-only — you can read this thread but not reply."
                : "This conversation is closed. The guest must start a new one."}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Column 3: what this conversation actually knows -----------------------

  renderRail() {
    const { selectedChat, selectedChatHistory } = this.state;

    if (!selectedChat) {
      return (
        <div
          className="chat-col"
          style={{ borderLeft: `1px solid ${C.border}`, background: C.surfaceAlt, padding: 20 }}
        >
          <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>Conversation</div>
          <div style={{ marginTop: 10, fontSize: 14, color: C.taupeLight, lineHeight: 1.55 }}>
            Details appear here once you open a thread.
          </div>
        </div>
      );
    }

    const open = selectedChat.chatStatus === "Open";
    const first = selectedChatHistory[0];
    const last = selectedChatHistory[selectedChatHistory.length - 1];
    const fromGuest = selectedChatHistory.filter((m) => m.fromUser?.role !== 1).length;

    const row = (label, value) => (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <span>{label}</span>
        <span style={{ color: C.ink, textAlign: "right" }}>{value}</span>
      </div>
    );

    return (
      <div
        className="chat-col chat-scroll"
        style={{
          borderLeft: `1px solid ${C.border}`, background: C.surfaceAlt,
          padding: 20, gap: 16,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          <Avatar name={selectedChat.chatFromName} size={64} />
          <div style={{ fontSize: 18, fontWeight: 500, color: C.ink, textAlign: "center" }}>
            {selectedChat.chatFromName || "Unknown guest"}
          </div>
          <Chip tone={open ? "good" : "neutral"}>{open ? "Open" : "Closed"}</Chip>
        </div>

        <div
          style={{
            marginTop: 16, background: "#fff", border: `1px solid ${C.tint}`,
            borderRadius: 18, padding: 18,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>This conversation</div>
          <div
            style={{
              marginTop: 12, display: "flex", flexDirection: "column",
              gap: 10, fontSize: 14, color: C.slate,
            }}
          >
            {row("Messages", selectedChatHistory.length)}
            {row("From the guest", fromGuest)}
            {row("Started", first ? this.formatDate(first.createdDate) : "—")}
            {row(
              "Last activity",
              last
                ? `${this.formatDate(last.createdDate)} · ${this.formatTime(last.createdDate)}`
                : "—"
            )}
          </div>
        </div>

        <div
          style={{
            marginTop: 16, padding: 14, borderRadius: 14,
            background: C.surface, border: `1px dashed ${C.sand}`,
            fontSize: 13, color: C.taupe, lineHeight: 1.55,
          }}
        >
          Guest profile, stay and folio are not part of the chat payload, so they
          are not shown here.
        </div>
      </div>
    );
  }

  render() {
    const { items, selectedChat, isLoaded, confirmClose } = this.state;
    const openCount = items.filter((c) => c.chatStatus === "Open").length;
    const canClose =
      canWrite() && selectedChat && selectedChat.chatStatus === "Open";

    return (
      <ConsolePage
        title="Guest chat"
        subtitle={
          isLoaded
            ? (() => {
                // items is ONE PAGE. Reporting its length as the total read as
                // "25 conversations" when there are 275.
                const total = (this.state.pagination && this.state.pagination.totalCount) || items.length;
                const shown = items.length;
                return total > shown
                  ? `${total} conversations · showing ${shown}`
                  : `${openCount} open · ${total} conversation${total === 1 ? "" : "s"}`;
              })()
            : "Loading conversations…"
        }
        actions={
          canClose ? (
            <Btn icon="check" onClick={() => this.setState({ confirmClose: true })}>
              Close thread
            </Btn>
          ) : null
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div className="chat-wrap">
          {this.renderList()}
          {this.renderThread()}
          {this.renderRail()}
        </div>

        <ConfirmDialog
          isOpen={confirmClose}
          toggle={() => this.setState({ confirmClose: false })}
          icon="check_circle"
          tone="neutral"
          title="Close this conversation?"
          body={
            selectedChat
              ? `${selectedChat.chatFromName || "This guest"} will no longer be able to reply in this thread.`
              : ""
          }
          tip="The guest can always start a new conversation from the app, so nothing is lost."
          confirmText="Close thread"
          onConfirm={this.confirmCloseThread}
        />
      </ConsolePage>
    );
  }
}

export default ChatList;
